import create from "zustand";
import { devtools } from "zustand/middleware";
import { json } from "d3-fetch";
import { timeDay } from "d3";
// import {addEffect} from 'react-three-fiber'

import {
  accessors,
  filterMulti,
  filterInDateRange,
  filterInArray,
  filterText,
  setFromArrayValues,
  FILTER_TARGETS_INTERSECTIONAL_NAME,
  LAYOUT,
  alphabeticalSort,
  uuidv4,
  filterWhenPredicate,
  uniqueByKey,
  CHART_DIMENSIONS,
} from "./utils/";

// import { filterGroups } from "./data/filter_groups";

export const fetchJSONData = async (filename) => {
  return json(filename);
};

const DEFAULT_CURATED_STORY = {
  selected: null,
  showNotes: false,
};

// TODO Move all to actions
const reducer = (state, { type, payload }) => {
  switch (type) {
    case `RESET_FILTER_DIMENSION`:
      return {
        ...state,
        filters: {
          ...state.filters,
          [payload]: {
            ...state.filters[payload],
            selected: [],
          },
        },
      };
    case `FILTER_UPDATE`:
      return {
        ...state,
        filters: payload,
        app: {
          ...state.app,
          selected: [], // reset clicked/hovered event
        },
        platform: {
          ...state.platform,
          story: DEFAULT_CURATED_STORY,
        },
      };
    case `FILTER_SEARCH_UPDATE`:
      return {
        ...state,
        filters: {
          ...state.filters,
          TEXT_SEARCH: {
            value: payload,
          },
        },
      };
    case `FILTER_TIME_UPDATE`:
      return {
        ...state,
        filters: {
          ...state.filters,
          TIME: {
            ...payload,
          },
        },
      };
    case `APP_UPDATE`:
      return {
        app: {
          ...state.app,
          ...payload,
        },
      };
    case `TIMELINE_UPDATE_SETTINGS`:
      return {
        app: {
          ...state.app,
          timeline: {
            ...state.app.timeline,
            ...payload,
          },
        },
      };
    case `TIMELINE_UPDATE_TRACKER`:
      return {
        app: {
          ...state.app,
          timeline: {
            ...state.app.timeline,
            tracker: {
              ...state.app.timeline.tracker,
              ...payload,
            },
          },
        },
      };
    case `CORPORATE_UPDATE_TRACKER`:
      return {
        app: {
          ...state.app,
          corporate: {
            ...state.app.corporate,
            time: {
              ...state.app.corporate.time,
              ...payload,
            },
          },
        },
      };
    case `CORPORATE_UPDATE_NARRATIVE_IDX`:
      return {
        app: {
          ...state.app,
          corporate: {
            ...state.app.corporate,
            narrativeIdx: payload,
          },
        },
      };
    case `CORPORATE_UPDATE_SHOW_HIGHLIGHTED_NAMES`:
      return {
        app: {
          ...state.app,
          corporate: {
            ...state.app.corporate,
            showOnlyHighlightedNames: payload,
          },
        },
      };
    case `TOGGLE_LIGHTBOX`:
      return {
        app: {
          ...state.app,
          lightbox: {
            show: payload.show,
            video: payload.video,
          },
        },
      };
    default:
      break;
  }
};

const initialState = {
  app: {
    // https://stackoverflow.com/questions/7838680/detecting-that-the-browser-has-no-mouse-and-is-touch-only/52854585#answer-52854585
    isMobile: window.matchMedia("(any-hover: none)").matches,
    showHeader: false,
    debugPlatform: false,
    ready: false,
    lightbox: {
      show: false,
    },
    explore: {
      showOnboarding: true,
      onboardingIdx: 0,
    },
    corporate: {
      narrativeIdx: 0,
      showOnlyHighlightedNames: true,
    },
    // holds reference to hovered events
    selected: [],
    // The 3D Visualization
    timeline: {
      camera: undefined,
      attributes: [],
      topView: false,
      orthographic: false,
      scales: {
        showLocalTime: LAYOUT.TIMELINE.SCALES.SHOW_LOCAL_TIME,
        width: CHART_DIMENSIONS.X.MAX,
      },
      tracker: {
        duration: LAYOUT.TIMELINE.TRACKER.WIDTH, // of a metric (here, eg days)
        start: LAYOUT.TIMELINE.FILTERS.TIME.START,
        end: timeDay.offset(
          LAYOUT.TIMELINE.FILTERS.TIME.START,
          LAYOUT.TIMELINE.TRACKER.WIDTH
        ),
      },
    },
  },
  // Responds and sets UI elements in the platform
  platform: {
    story: DEFAULT_CURATED_STORY,
  },
  data: {
    events: [],
    financial: [],
    corporate: [],
  },
};

export const useStore = create(
  devtools((set, get) => ({
    ...initialState,
    actions: {
      changeOnboardingIdx(idx) {
        const { app } = get();
        set({
          app: { ...app, explore: { ...app.explore, onboardingIdx: idx } },
        });
      },
      init(camera) {
        // called when the scene is initialized
        const { app } = get();
        set({ app: { ...app, timeline: { ...app.timeline, camera } } });
      },
      hideOnboarding() {
        const { app } = get();
        set({
          app: {
            ...app,
            explore: {
              ...app.explore,
              showOnboarding: false,
            },
          },
        });
      },
      hideStoryNotes() {
        const { platform } = get();
        set({
          platform: {
            ...platform,
            story: {
              ...platform.story,
              showNotes: false,
            },
          },
        });
      },
      onAssociationClick(target) {
        const { filters, app } = get();
        const associationToFilter = target.eventObject.userData;
        // console.log(association.eventObject);
        // return;
        set({
          filters: {
            ...filters,
            FIELD_OF_OPERATION: {
              ...filters.FIELD_OF_OPERATION,
              selected: [associationToFilter],
            },
          },
          app: {
            ...app,
            timeline: {
              ...app.timeline,
              orthographic: true,
            },
          },
        });
      },
      onTimelineFocus(d) {
        const { app } = get();
        set({
          app: {
            ...app,
            selected: d ? d : [],
          },
        });
      },
      resetAllFilters() {
        const {
          data: { events, targets, countries, categoryFilters },
          platform,
        } = get();
        set({
          filters: initializeFilters({
            events,
            targets,
            countries,
            groups: categoryFilters,
          }),
          platform: {
            ...platform,
            story: DEFAULT_CURATED_STORY,
          },
        });
      },
      exitPlatform() {
        const { app } = get();
        set({
          app: {
            ...app,
            explore: {
              showOnboarding: true,
              onboardingIdx: 0,
            },
            timeline: {
              ...app.timeline,
              topView: false,
              orthographic: false,
              scales: {
                showLocalTime: LAYOUT.TIMELINE.SCALES.SHOW_LOCAL_TIME,
                width: CHART_DIMENSIONS.X.MAX,
              },
            },
          },
        });
      },
      selectPlatformStory(story) {
        const {
          data: { events, targets, countries, categoryFilters },
          platform,
        } = get();
        // first re-initialize all filters
        const filters = initializeFilters({
          events,
          targets,
          countries,
          groups: categoryFilters,
        });
        const nextFilters = story.filters || {}; // latter in the case of no mutation
        // then, go into them one by one and change them
        Object.entries(nextFilters).forEach(([key, { selected }]) => {
          filters[key].selected = selected;
        });
        set({
          filters,
          platform: {
            ...platform,
            story: {
              selected: story,
              showNotes: !!story.notes,
            },
          },
        });
      },
      showHeader(s) {
        const { app } = get();
        set({
          app: {
            ...app,
            showHeader: s,
          },
        });
      },
      showOnboarding() {
        const { app } = get();
        set({
          app: {
            ...app,
            explore: {
              ...app.explore,
              showOnboarding: true,
            },
          },
        });
      },
      updateTimelineWidth(width) {
        console.log("updating timeline width", width);
        const { app } = get();
        set({
          app: {
            ...app,
            timeline: {
              ...app.timeline,
              scales: {
                ...app.timeline.scales,
                width,
              },
            },
          },
        });
      },
      updateTimelineAttributes(a) {
        const { app } = get();
        // deduplicate first
        const n = uniqueByKey(a, accessors.id);
        console.log("duplicate", a.length, "deduped", n.length);
        set({
          app: {
            ...app,
            timeline: {
              ...app.timeline,
              attributes: n,
            },
          },
        });
      },
      updateTimelineConfig() {},
    },
    initMediaData: async (filename) => {
      return Promise.all([fetchJSONData(filename)])
        .then(([data]) => {
          set((state) => ({
            data: {
              ...state.data,
              media: data,
            },
          }));
        })
        .catch((e) => {
          // console.warn("Couldn't load media data");
        });
    },
    initEventData: async (filename) => {
      const fetchEventData = async (filename) => {
        return fetchJSONData(filename).then(
          ({
            events,
            targets,
            countries,
            event_type_filters: categoryFilters,
            categories,
          }) => {
            const getColorFromEventType = (d) =>
              categories.find((c) => c.name === accessors.event.type(d))?.rgb ||
              0xffffff;
            const getOpacityFromEventType = (d) =>
              categories.find((c) => c.name === accessors.event.type(d))
                ?.opacity || 1;
            return {
              events: events.map((d) => ({
                ...d,
                color: getColorFromEventType(d),
                // TODO This way, there isn't differential opacity per-field
                opacity: getOpacityFromEventType(d),
                opacityPerAssociation: accessors.opacity(d), // this is an array
                // keep all associations, because the key is then modified in attributes.js
                allAssociations: accessors.association(d),
                date: {
                  ...d.date,
                  start: new Date(accessors.date.start(d)),
                },
              })),
              targets,
              countries,
              categoryFilters,
              categories,
            };
          }
        );
      };

      return Promise.all([fetchEventData(filename)]).then(
        ([{ events, targets, countries, categoryFilters, categories }]) => {
          // We filter data only based on the global start and end time
          // We also add a UUID so it's possible to reference graphical elements
          // between our app and WebGL object instances
          const filteredEvents = filterMulti(
            events,
            filterInDateRange(
              [
                LAYOUT.TIMELINE.FILTERS.TIME.START,
                LAYOUT.TIMELINE.FILTERS.TIME.END,
              ],
              accessors.date.start
            )
          ).map((e) => ({ ...e, uuid: uuidv4() }));

          set((state) => ({
            data: {
              ...state.data,
              events: filteredEvents,
              targets,
              countries,
              categoryFilters,
              categories,
            },
            // TODO: Find convention for selectively showing
            // closed filters in filter groups.
            // At the moment this is happening by detecting the
            // all property
            filters: initializeFilters({
              events: filteredEvents,
              targets,
              countries,
              groups: categoryFilters,
            }),
          }));
        }
      );
    },
    dispatch: (args) => set((state) => reducer(state, args)),
  }))
);

// this re-initializes the filters
// filter groups are generated from the structure of this object
const initializeFilters = ({
  events,
  targets = [],
  countries = [],
  groups = [],
}) => {
  // dictionary between targets and fields of operation
  const targetsByField = targets.reduce((acc, curr) => {
    // Prepend intersectional to add a separate filter group
    // for targets that appear across fields of operation.
    // This value appears in our ordered_data driver file,
    // which is then consolidated into events.json
    [FILTER_TARGETS_INTERSECTIONAL_NAME, ...countries].forEach((country) => {
      acc = {
        ...acc,
        [country]: !acc[country] ? [] : [...acc[country], curr[country]],
      };
    });
    return acc;
  }, {});

  return {
    TEXT_SEARCH: {
      value: "",
    },
    TIME: {
      start: LAYOUT.TIMELINE.FILTERS.TIME.START,
      end: LAYOUT.TIMELINE.FILTERS.TIME.END,
    },
    EVENT_TYPE: {
      all: [...setFromArrayValues(events, accessors.event.type)],
      selected: [],
      groups: groups.map((g) => ({ ...g, opaque: true, isOpen: true })),
      isOpen: true,
    },
    FIELD_OF_OPERATION: {
      all: [...setFromArrayValues(events, accessors.association)],
      isOpen: true,
      selected: [],
    },
    TARGETS: {
      isOpen: true,
      all: [...setFromArrayValues(events, accessors.target.name)],
      selected: [],
      groups: Object.entries(targetsByField).map(([key, value]) => {
        // sort alphabetically (if not intersectional targets)
        const sortedValues =
          key === FILTER_TARGETS_INTERSECTIONAL_NAME
            ? value
            : value.sort(alphabeticalSort);
        return {
          isOpen: true,
          name: key,
          children: sortedValues
            .filter((v) => v !== "")
            .map((v) => ({ name: v })),
        };
      }),
    },
  };
};

// Filter attributes will also apply formatting on an attribute's type
// (targeted/contextual/global)
export const generateFilterFunctionArray = (
  filters,
  config = { filterAttributes: false }
) => {
  return Object.entries(filters).flatMap(([key, val]) => {
    if (Array.isArray(val?.selected) && !val.selected?.length) {
      // the case in which there are no filters for that dimension

      // essentially passes a no-op filtering function that always evaluates true
      return () => true;
    }
    switch (key) {
      case `TIME`:
        const { start, end } = val;
        return filterInDateRange([start, end], accessors.date.start);
      case `TEXT_SEARCH`:
        const { value: searchTerm } = val;
        return filterText(searchTerm, LAYOUT.TIMELINE.FILTERS.TEXT_FIELDS);
      case `TARGETS`:
        return filterInArray(val.selected, accessors.target.name);
      case `FIELD_OF_OPERATION`:
        return filterInArray(val.selected, accessors.association);
      case `EVENT_TYPE`:
        if (config?.filterAttributes) {
          // secondary filters are ones that are applied when the
          // predicate condition is matched.
          // some filters are of the form CATEGORY:targeted
          // (as defined in filter_groups.json)
          // This indicates a secondary filtering if-f the data point
          // matches CATEGORY.
          // This allows us to filter by attribute type
          // ie. (circles / lines on timeline / lines on floor)
          const attributeFilters = val.selected
            .map((v) => v.split(":"))
            .filter((v) => v.length === 2)
            .reduce(
              (acc, [eventType, attributeType]) => ({
                ...acc,
                [eventType]: acc[eventType]
                  ? [attributeType, ...acc[eventType]]
                  : [attributeType],
              }),
              {}
            );

          // which event type filters have a shape specifier?
          return Object.values(attributeFilters).length > 0
            ? [
                filterInArray(
                  [...val.selected, ...Object.keys(attributeFilters)],
                  accessors.event.type
                ),
                ...Object.entries(attributeFilters).map(
                  ([eventType, attributeTypes]) =>
                    filterWhenPredicate(
                      accessors.event.type,
                      eventType,
                      accessors.type,
                      attributeTypes
                    )
                ),
              ]
            : filterInArray(val.selected, accessors.event.type);
        }
        return filterInArray(val.selected, accessors.event.type);
      default:
        return () => true;
    }
  });
};
